import type { QueryClient, QueryKey } from 'react-query';

import { apiQueryKey, useApiQuery } from '../../api/useApi';
import type { Customer } from '../../pages/cardex/api/useCustomer';

import { invaliderNombreConversationsNonLues } from './useNombreConversationsNonLues';

/**
 * La liste des conversations — #82.
 *
 * Hook maison plutôt que `useMessagingCustomers` : celui-là vit dans
 * `pages/cardex/api/`, que la frontière du socle interdit d'atteindre (#23). Mais
 * ce n'est pas qu'une contrainte subie — deux de ses réglages sont **faux au
 * mobile**, et les reprendre aurait cassé un critère d'acceptation :
 *
 * - **`keepPreviousData: true`** rend les données de la clé précédente pendant le
 *   chargement de la nouvelle. Comme `facility_id` est dans l'URL, donc dans la
 *   clé, c'est très exactement le mécanisme qui afficherait les conversations de
 *   l'hôtel A sous l'en-tête de l'hôtel B après une bascule. Le web s'en accommode
 *   avec une invalidation globale et un `setTimeout(500)` ; #25 a tranché que sur
 *   réseau dégradé ces 500 ms sont une fiction. Ici la donnée périmée est
 *   impossible **par construction**, pas par chronométrage.
 * - **`refetchOnMount: false`** convient à une liste qu'on ne quitte jamais
 *   vraiment. Au mobile, revenir de la conversation à la liste (#16 : « retour =
 *   liste ») doit montrer l'état réel, pas celui d'il y a dix minutes.
 *
 * **Le masquage des fermées est hérité gratuitement.** `tab=inbox` déclenche
 * `scopeInInbox()` côté serveur — un `whereDoesntHave('folders', hides_from_inbox)`.
 * Les 1 347 conversations fermées, 97 spams et 41 corbeilles ne sortent jamais, et
 * le mobile n'écrit aucun filtre pour ça (#30).
 *
 * **Les trois onglets sont des filtres SERVEUR.** La maquette filtrait en
 * JavaScript sur la page courante ; sur une liste paginée à 25, « Non lus »
 * n'aurait montré que les non-lus des 25 premières conversations, en annonçant le
 * contraire. `CustomerController` applique `unread` et `assigned_to_me` en base.
 */

export type OngletConversations = 'non_lus' | 'tous' | 'a_moi';

/** Ce que chaque onglet demande au serveur. `tous` ne filtre rien. */
const FILTRE_SERVEUR: Record<OngletConversations, string> = {
    non_lus: 'unread',
    tous: '',
    a_moi: 'assigned_to_me',
};

export interface PageConversations {
    data: Customer[];
    meta: { current_page: number; last_page: number; total: number; per_page: number };
}

interface Options {
    facilityId: number | null;
    onglet: OngletConversations;
    page?: number;
    /**
     * L'onglet SERVEUR : `inbox` masque les fermées, `closed` ne montre qu'elles.
     *
     * Reste `inbox` par défaut — c'est lui qui porte le masquage de #30, et rendre
     * le paramètre optionnel ne doit pas rendre le masquage optionnel.
     */
    dossier?: 'inbox' | 'closed';
    /**
     * Nom, prénom ou e-mail — recherche serveur DANS l'onglet courant, jamais à sa
     * place (`CustomerController` combine `search` et `filter`). Absent ou vide :
     * rien n'est écrit, la clé React Query n'en change pas pour rien.
     */
    search?: string;
}

/**
 * Construit l'URL. Exportée pour que le test puisse l'affirmer directement :
 * c'est elle qui porte les deux critères d'acceptation « aucun filtre client » et
 * « la bascule ne montre jamais les données de l'autre ».
 */
export function urlConversations({ facilityId, onglet, page = 1, dossier = 'inbox', search }: Options): string {
    if (facilityId === null) return '';

    const params = new URLSearchParams({
        page: String(page),
        facility_id: String(facilityId),
        // C'est LUI qui masque les fermées, les spams et la corbeille (#30) —
        // et, en `closed`, LUI qui les retrouve (#85).
        tab: dossier,
        sortField: 'last_message_at',
        sortDirection: 'desc',
    });

    const filtre = FILTRE_SERVEUR[onglet];
    if (filtre) params.set('filter', filtre);

    // `URLSearchParams` encode déjà le terme correctement (espaces, `&`, `#`…) —
    // aucune concaténation manuelle qui casserait l'URL sur un nom accentué.
    if (search) params.set('search', search);

    return `/customers-messaging?${params.toString()}`;
}

/**
 * La recherche dans TOUT le cardex — évo Odomo du 09/10/2026.
 *
 * `urlConversations` ne trouve que les clients qui ont déjà une conversation
 * (`tab=inbox` garde les messages reçus ou manuels). `search_all_folders=1` est le
 * chemin du globe de la messagerie web : avec un terme, le serveur ignore onglet,
 * dossiers et `manual_only`, et cherche dans toutes les fiches de l'hôtel ou du
 * groupe (nom, prénom, e-mail, téléphone). Sans terme, rien n'est demandé.
 */
export function urlAutresClients({ facilityId, search }: { facilityId: number | null; search: string }): string {
    if (facilityId === null || search === '') return '';

    const params = new URLSearchParams({
        page: '1',
        facility_id: String(facilityId),
        search_all_folders: '1',
        search,
    });

    return `/customers-messaging?${params.toString()}`;
}

export function useAutresClients(facilityId: number | null, search: string) {
    const url = urlAutresClients({ facilityId, search });
    return useApiQuery<PageConversations>(url, {}, {
        refetchOnWindowFocus: false,
        enabled: url !== '',
    });
}

/**
 * Une requête de la liste des conversations, quelles que soient page, onglet ou
 * dossier — audit 2026-09-23 (A7).
 *
 * Le seul prédicat : il était recopié dans quatre fichiers, et une route qui
 * change n'en aurait corrigé qu'une partie. Typé sur `QueryKey` pour se passer
 * tel quel en `predicate` à React Query.
 */
export function estRequeteConversations(query: { queryKey: QueryKey }): boolean {
    const cle = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
    return typeof cle === 'string' && cle.startsWith('/customers-messaging');
}

/** La page telle que la conversation `customerId` lue la rend, ou la MÊME page si rien ne change. */
function pageApresLecture(page: PageConversations | undefined, url: unknown, customerId: string) {
    if (!page || !Array.isArray(page.data)) return page;
    const index = page.data.findIndex((c) => String(c.id) === customerId);
    if (index === -1) return page;

    const requete = typeof url === 'string' ? url.split('?')[1] ?? '' : '';
    if (new URLSearchParams(requete).get('filter') === 'unread') {
        return {
            ...page,
            data: page.data.filter((_, i) => i !== index),
            meta: page.meta ? { ...page.meta, total: Math.max(0, page.meta.total - 1) } : page.meta,
        };
    }

    if (!page.data[index].unread_count) return page;
    return { ...page, data: page.data.map((c, i) => (i === index ? { ...c, unread_count: 0 } : c)) };
}

/**
 * La conversation ouverte devient lue DANS LE CACHE de la liste — audit 2026-09-23 (A1).
 *
 * `/get-messages` marque lu côté serveur. On invalidait la liste pour que la ligne
 * sorte du gras : chaque retour de fil relançait `/customers-messaging`, qui coûte
 * 0,6 s sur un hôtel et 13 à 40 s sur un groupe. La seule chose qui a changé est
 * connue ici, sans réseau : son compteur tombe à 0, et « Non lus » la perd.
 *
 * Page par page plutôt que `setQueriesData` : une page où rien ne change n'est
 * pas réécrite, et une page réécrite garde son `dataUpdatedAt`. Sinon « Mis à
 * jour il y a… » et la borne de 60 s du premier plan croiraient la liste
 * rechargée alors qu'elle ne l'a pas été.
 *
 * **Une réponse de liste encore en vol est annulée d'abord** (relecture I1). La
 * liste démontée pendant un rechargement long le laisse finir (React Query 3) :
 * sa réponse, lue côté serveur AVANT l'ouverture du fil, écraserait la correction
 * et remettrait la ligne dans « Non lus ». L'invalider ne suffit pas : la réponse
 * efface elle-même l'invalidation. `cancelQueries` rend l'état d'avant le
 * chargement, `dataUpdatedAt` compris : une page périmée le reste, et se
 * recharge au remontage de la liste.
 *
 * La page annulée est ensuite INVALIDÉE, sans rechargement (relecture finale) :
 * encore fraîche (l'utilisateur venait de tirer pour actualiser), son ancien
 * `dataUpdatedAt` la ferait passer pour à jour, et le rafraîchissement demandé
 * serait perdu. Elle se recharge ainsi au remontage de la liste.
 *
 * Une page déjà invalidée le reste aussi : `setQueryData` efface l'invalidation,
 * qui est donc reposée, sans rechargement.
 *
 * Synchrone quand rien n'est en vol : aucun `await` n'est alors traversé.
 */
export async function marquerConversationLue(queryClient: QueryClient, customerId: string): Promise<void> {
    const filtre = { predicate: estRequeteConversations };
    const enVol = queryClient
        .getQueriesData<PageConversations>(filtre)
        .filter(([cle]) => queryClient.getQueryState(cle)?.isFetching);
    if (enVol.length > 0) {
        await Promise.all(enVol.map(([cle]) => queryClient.cancelQueries({ queryKey: cle, exact: true })));
    }

    for (const [cle, page] of queryClient.getQueriesData<PageConversations>(filtre)) {
        const suivante = pageApresLecture(page, Array.isArray(cle) ? cle[0] : cle, customerId);
        if (suivante === page) continue;
        const etat = queryClient.getQueryState(cle);
        queryClient.setQueryData(cle, suivante, { updatedAt: etat?.dataUpdatedAt });
        if (etat?.isInvalidated) {
            void queryClient.invalidateQueries({ queryKey: cle, exact: true, refetchActive: false });
        }
    }

    // Après `setQueryData`, qui effacerait l'invalidation.
    for (const [cle] of enVol) {
        void queryClient.invalidateQueries({ queryKey: cle, exact: true, refetchActive: false });
    }

    // Le badge de navigation compte des CONVERSATIONS, pas des messages : on ne
    // sait pas d'ici si celle-ci en faisait encore partie pour un autre agent —
    // un aller-retour serveur, pas une déduction locale.
    invaliderNombreConversationsNonLues(queryClient);
}

/** Marqueur : cette page ne peut pas être corrigée sur place, il lui faut un aller-retour serveur. */
const A_INVALIDER = Symbol('page à invalider');

/** La page telle que le marquage NON LU la rend — l'inverse de `pageApresLecture`. */
function pageApresMarquageNonLu(
    page: PageConversations | undefined,
    url: unknown,
    customerId: string,
): PageConversations | undefined | typeof A_INVALIDER {
    if (!page || !Array.isArray(page.data)) return page;

    const requete = typeof url === 'string' ? url.split('?')[1] ?? '' : '';
    if (new URLSearchParams(requete).get('filter') === 'unread') {
        // La ligne a pu sortir de cette page filtrée pendant qu'elle était lue —
        // on ne sait pas où la réinsérer sans casser le tri « dernier message
        // reçu ». Mieux vaut un aller-retour serveur qu'une insertion hasardeuse.
        return A_INVALIDER;
    }

    const index = page.data.findIndex((c) => String(c.id) === customerId);
    if (index === -1) return page;
    if (page.data[index].unread_count) return page; // déjà non lue, rien à faire
    return { ...page, data: page.data.map((c, i) => (i === index ? { ...c, unread_count: 1 } : c)) };
}

/**
 * L'inverse de `marquerConversationLue` — #86.
 *
 * Même idiome, même raison d'être : `/customers/{id}/mark-unread` répond, mais la
 * liste garde sa donnée 5 min et la ligne resterait affichée comme lue tant que
 * personne ne la recharge. On la corrige SUR PLACE, sans requête.
 *
 * Les mêmes précautions s'appliquent, dans le même ordre : annuler une réponse
 * encore en vol AVANT d'écrire (elle écraserait la correction en arrivant), puis
 * réinvalider les pages qu'on vient de patcher pour qu'un « Actualiser » les
 * revoie comme périmées plutôt que fraîches.
 *
 * **Annule aussi `/get-messages/{customerId}`, en tout premier.** C'est LE piège
 * du geste (voir `useFilConversation`) : cette route marque TOUT lu à CHAQUE
 * réponse, et le fil la rappelle toutes les 30 s tant qu'on le regarde. Une
 * réponse de ce poll encore en vol au moment du tap écraserait notre remise à non
 * lu dès qu'elle atterrirait — `messages.data` changerait de référence, l'effet de
 * `useFilConversation` se redéclencherait, et `marquerConversationLue` repasserait
 * la ligne en lu sous nos yeux. Le retour à la liste, côté appelant, est ce qui
 * arrête le poll pour de bon ; l'annulation ici couvre la fenêtre d'avant.
 */
export async function marquerConversationNonLue(queryClient: QueryClient, customerId: string): Promise<void> {
    await queryClient.cancelQueries({
        queryKey: apiQueryKey(`/get-messages/${customerId}`),
        exact: true,
    });

    const filtre = { predicate: estRequeteConversations };
    const enVol = queryClient
        .getQueriesData<PageConversations>(filtre)
        .filter(([cle]) => queryClient.getQueryState(cle)?.isFetching);
    if (enVol.length > 0) {
        await Promise.all(enVol.map(([cle]) => queryClient.cancelQueries({ queryKey: cle, exact: true })));
    }

    const aInvalider = enVol.map(([cle]) => cle);

    for (const [cle, page] of queryClient.getQueriesData<PageConversations>(filtre)) {
        const suivante = pageApresMarquageNonLu(page, Array.isArray(cle) ? cle[0] : cle, customerId);
        if (suivante === A_INVALIDER) {
            aInvalider.push(cle);
            continue;
        }
        if (suivante === page) continue;
        const etat = queryClient.getQueryState(cle);
        queryClient.setQueryData(cle, suivante, { updatedAt: etat?.dataUpdatedAt });
        if (etat?.isInvalidated) aInvalider.push(cle);
    }

    // Après `setQueryData`, qui effacerait l'invalidation.
    for (const cle of aInvalider) {
        void queryClient.invalidateQueries({ queryKey: cle, exact: true, refetchActive: false });
    }

    invaliderNombreConversationsNonLues(queryClient);
}

export function useConversations(options: Options) {
    return useApiQuery<PageConversations>(urlConversations(options), {}, {
        // Ni keepPreviousData ni refetchOnMount: false — voir l'en-tête.
        // Le retour au premier plan est géré par l'écran (`ListeConversations.actualiser`),
        // qui repart en page 1.
        refetchOnWindowFocus: false,
        enabled: options.facilityId !== null,
    });
}
